import React from 'react';
import s from './Select.module.css';
import ms from './MultiSelect.module.css';
import { ReactComponent as ArrowIcon } from '../../icons/select_arrow.svg';

export function MultiSelect({
  value = [],
  onChange,
  ariaLabel,
  className = '',
  placeholder = 'Выберите…',
  emptyMessage = 'Нет доступных вариантов',
  disabled = false,
  children,
}) {
  const containerRef = React.useRef(null);
  const [open, setOpen] = React.useState(false);

  const options = React.useMemo(() => {
    return React.Children.toArray(children)
      .filter(React.isValidElement)
      .map((child) => ({
        value: child.props.value,
        label: child.props.children,
        disabled: child.props.disabled,
      }));
  }, [children]);

  const selectedSet = React.useMemo(
    () => new Set((value || []).map((v) => String(v))),
    [value]
  );

  React.useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      const root = containerRef.current;
      if (root && !root.contains(e.target)) setOpen(false);
    };
    const handleKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [open]);

  const toggleValue = (optionValue) => {
    const key = String(optionValue);
    const next = selectedSet.has(key)
      ? (value || []).filter((v) => String(v) !== key)
      : [...(value || []), optionValue];
    onChange?.(next);
  };

  const handleKeyDown = (e) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const boxClass = `${s.box} ${open ? s.boxOpen : ''} ${className}`.trim();

  return (
    <div ref={containerRef} className={boxClass}>
      <button
        type="button"
        className={`${s.display} ${disabled ? s.displayDisabled : ''}`}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => !prev);
        }}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
      >
        <span className={`${s.displayText} ${ms.placeholder}`}>
          {placeholder}
        </span>
      </button>

      {open && (
        <ul role="listbox" aria-multiselectable="true" className={s.menu}>
          {options.length === 0 && (
            <li className={ms.empty}>{emptyMessage}</li>
          )}
          {options.map((opt, idx) => {
            const checked = selectedSet.has(String(opt.value));
            const cls = [
              ms.option,
              opt.disabled ? s.optionDisabled : '',
            ]
              .filter(Boolean)
              .join(' ');
            return (
              <li
                key={opt.value ?? idx}
                role="option"
                aria-selected={checked}
                className={cls}
              >
                <label className={ms.optionLabel}>
                  <input
                    type="checkbox"
                    className={ms.checkbox}
                    checked={checked}
                    disabled={opt.disabled}
                    onChange={() => {
                      if (opt.disabled) return;
                      toggleValue(opt.value);
                    }}
                  />
                  <span className={ms.optionText}>{opt.label}</span>
                </label>
              </li>
            );
          })}
        </ul>
      )}

      <span className={s.icon} aria-hidden="true">
        <ArrowIcon />
      </span>
    </div>
  );
}
